import React, { forwardRef, useCallback, type ReactNode } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import { DropdownItem } from '@atlaskit/dropdown-menu';
import { SpotlightPulse } from '@atlaskit/onboarding';
import { token } from '@atlaskit/tokens';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { ISSUE_FIELD_SPOTLIGHT_PREFIX } from '@atlassian/jira-issue-view-common-constants/src/view-constants.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';

import GlobalSpotlightTargetDeprecated from '@atlassian/jira-servicedesk-common/src/ui/global-spotlight-target/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';

export type ExtraAttributes = {
	[key: string]: string | null | number | null;
} | null;

type BaseProps = {
	icon: ReactNode;
	id: string;
	extraAttributes?: ExtraAttributes;
	testId?: string;
	onClick: () => void;
};

type Props = {
	hasGlobalSpotlightTarget?: boolean;
	hasSpotlightPulse?: boolean;
	noWrapper?: boolean;
	isQuickActionsListView?: boolean;
} & BaseProps & {
		label?: string;
		compactMode?: boolean;
	};

const View = forwardRef<HTMLElement, Props>((props: Props, ref) => {
	const {
		id,
		icon,
		onClick,
		extraAttributes = {},
		label = '',
		compactMode = true,
		testId = '',
		hasGlobalSpotlightTarget = false,
		hasSpotlightPulse = false,
		noWrapper = false,
		isQuickActionsListView,
	} = props;

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onClickWithAnalytics = useCallback(() => {
		onClick && onClick();
		fireUIAnalytics(
			createAnalyticsEvent({}),
			'button clicked',
			'showChildIssuesInlineCreateButton',
		);
	}, [createAnalyticsEvent, onClick]);

	const themedButton = (
		<Button
			aria-label={label}
			spacing={compactMode ? 'none' : 'default'}
			iconBefore={<IconWrapper compactMode={compactMode}>{icon}</IconWrapper>}
			onClick={onClickWithAnalytics}
			ref={ref}
			interactionName="auto-generated.jira-issue-view-foundation.quick-add.quick-add-item.button"
		>
			{!compactMode && label}
		</Button>
	);

	/* NOTE: These GlobalSpotlightTargets (deprecated) can be used throughout Jira not just within
	 * the current package. They're often used for onboarding or changeboarding
	 * experiences and allow an Atlaskit spotlight to be attached to page elements.
	 * If you need to change this, make sure you check where the target ID is being used
	 * and contact the relevant team as necessary. This Sourcegraph link should help :)
	 * http://go/src/ISSUE_FIELD_SPOTLIGHT_PREFIX|global-spotlight-target-
	 */
	const buttonItemWithSpotlight = (
		<GlobalSpotlightTargetDeprecated id={`${ISSUE_FIELD_SPOTLIGHT_PREFIX}${id}`}>
			{themedButton}
		</GlobalSpotlightTargetDeprecated>
	);
	const style = {
		position: 'inherit',
		marginRight: token('space.200', '16px'),
	} as const;

	if (noWrapper) {
		return themedButton;
	}

	if (isQuickActionsListView && fg('quick_actions_menu_ga')) {
		/* eslint-disable-next-line react-hooks/rules-of-hooks */
		const onClickWithAnalyticsForDropdownItems = useCallback(() => {
			onClick && onClick();
			fireUIAnalytics(
				createAnalyticsEvent({}),
				'button clicked',
				'showChildIssuesInlineCreateButton',
			);
		}, [createAnalyticsEvent, onClick]);

		if (fg('add_error_boundary_for_quick_actions_add_button')) {
			return (
				<UFOSegment name="quick-actions-mapped-issue-actions">
					<JSErrorBoundary
						id="quick-actions-mapped-issue-actions"
						packageName="jiraQuickActions"
						teamName="bento"
						fallback="unmount"
					>
						<DropdownItem
							key={id}
							onClick={onClickWithAnalyticsForDropdownItems}
							testId={testId}
							elemBefore={icon}
							ref={ref}
						>
							{label}
						</DropdownItem>
					</JSErrorBoundary>
				</UFOSegment>
			);
		}

		return (
			<DropdownItem
				key={id}
				onClick={onClickWithAnalyticsForDropdownItems}
				testId={testId}
				elemBefore={icon}
				ref={ref}
			>
				{label}
			</DropdownItem>
		);
	}

	if (hasGlobalSpotlightTarget && hasSpotlightPulse) {
		return (
			<ButtonWrapper {...extraAttributes} data-testid={testId}>
				<GlobalSpotlightTargetDeprecated id={`${ISSUE_FIELD_SPOTLIGHT_PREFIX}${id}`}>
					{/* eslint-disable-next-line jira/react/no-style-attribute, @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766 */}
					<SpotlightPulse style={style}>{themedButton}</SpotlightPulse>
				</GlobalSpotlightTargetDeprecated>
			</ButtonWrapper>
		);
	}

	return (
		<ButtonWrapper {...extraAttributes} data-testid={testId}>
			{hasGlobalSpotlightTarget ? buttonItemWithSpotlight : themedButton}
		</ButtonWrapper>
	);
});

export default View;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonWrapper = styled.span({
	marginRight: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconWrapper = styled.span<{ compactMode: boolean }>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	padding: (props) => (props.compactMode ? `${token('space.050', '4px')}` : 0),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	marginLeft: (props) => (props.compactMode ? 0 : token('space.negative.050', '-4px')),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	marginRight: (props) => (props.compactMode ? 0 : token('space.negative.025', '-2px')),
});
